import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Check, Upload, Loader2, AlertCircle, Presentation, FileText, Image } from 'lucide-react';
import uploadService from '../../../services/uploadService';
import { toast } from 'react-hot-toast';

const ACCEPTED_EXTENSIONS = '.pdf,.pptx,.ppt,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif';
const MAX_FILE_SIZE_MB = 100;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

// Custom Select component
const Select = ({ value, onValueChange, children, placeholder }) => {
  const [isOpen, setIsOpen] = useState(false);
  const selectRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (selectRef.current && !selectRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const options = React.Children.toArray(children);
  const selectedOption = options.find(opt => opt.props.value === value);

  return (
    <div className="relative" ref={selectRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white text-sm hover:border-white/20 transition-colors"
      >
        <span className={selectedOption ? '' : 'text-gray-500'}>
          {selectedOption ? selectedOption.props.children : placeholder}
        </span>
        <svg className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {isOpen && (
        <div className="absolute z-50 mt-1 w-full bg-[#1a1a2e] border border-white/10 rounded-lg shadow-xl py-1 max-h-60 overflow-y-auto">
          {options.map((option) => (
            <button
              key={option.props.value}
              type="button"
              onClick={() => {
                onValueChange(option.props.value);
                setIsOpen(false);
              }}
              className={`w-full px-4 py-2 text-left text-sm hover:bg-white/10 transition-colors ${
                option.props.value === value ? 'text-orange-400' : 'text-gray-300'
              }`}
            >
              {option.props.children}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const SelectItem = ({ value, children }) => null;

export default function PresentationElementModal({
  isOpen,
  onClose,
  onSave,
  presentation,
  folders,
  currentFolder,
  skipFolderSelect = false,
  readOnly = false,
  onCopyToMyElements
}) {
  const [formData, setFormData] = useState({
    name: '',
    folder_id: currentFolder?.id || '',
    fileUrl: '',
    fileType: '' // 'pdf' or 'image'
  });

  const [isUploading, setIsUploading] = useState(false);
  const [isConverting, setIsConverting] = useState(false);
  const [error, setError] = useState('');
  const [hasInitialized, setHasInitialized] = useState(false);
  const fileInputRef = useRef(null);

  // Initialize form data only when modal opens, not on every prop change
  useEffect(() => {
    if (isOpen && !hasInitialized) {
      if (presentation) {
        setFormData({
          name: presentation.name || '',
          folder_id: presentation.folder_id || currentFolder?.id || '',
          fileUrl: presentation.fileUrl || '',
          fileType: presentation.fileType || ''
        });
      } else {
        setFormData({
          name: '',
          folder_id: currentFolder?.id || '',
          fileUrl: '',
          fileType: ''
        });
      }
      setError('');
      setHasInitialized(true);
    }
  }, [isOpen, presentation, currentFolder, hasInitialized]);

  // Reset initialization flag when modal closes
  useEffect(() => {
    if (!isOpen) {
      setHasInitialized(false);
    }
  }, [isOpen]);

  const getFileType = (filename) => {
    const ext = filename.split('.').pop()?.toLowerCase();
    if (ext === 'pdf') return 'pdf';
    if (['pptx', 'ppt'].includes(ext)) return 'pptx';
    if (['doc', 'docx'].includes(ext)) return 'docx';
    if (['xls', 'xlsx'].includes(ext)) return 'xlsx';
    if (['jpg', 'jpeg', 'png', 'gif'].includes(ext)) return 'image';
    return null;
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Clear input immediately so same file can be re-selected
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    setError('');

    // Validate size
    if (file.size > MAX_FILE_SIZE_BYTES) {
      setError(`This file is too large. Please upload a file under ${MAX_FILE_SIZE_MB} MB.`);
      return;
    }

    // Validate type by extension
    const fileType = getFileType(file.name);
    if (!fileType) {
      setError('Unsupported file type. Please upload PDF, PPTX, PPT, DOC, DOCX, XLS, XLSX, JPG, JPEG, PNG, or GIF.');
      return;
    }

    setIsUploading(true);
    if (fileType === 'pptx') setIsConverting(true);

    try {
      const response = await uploadService.uploadFile(file);
      if (response?.url) {
        setFormData(prev => ({
          ...prev,
          fileUrl: response.url,
          // PPTX files are converted to PDF on the server
          fileType: fileType === 'pptx' ? 'pdf' : fileType,
          name: prev.name || file.name.replace(/\.[^/.]+$/, '')
        }));
        toast.success(fileType === 'pptx' ? 'Presentation converted & uploaded' : 'File uploaded');
      }
    } catch (err) {
      console.error('Failed to upload file:', err);
      setError(fileType === 'pptx'
        ? 'Failed to convert PowerPoint file. Please try exporting to PDF manually.'
        : 'Failed to upload file. Please try again.');
    } finally {
      setIsUploading(false);
      setIsConverting(false);
    }
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      setError('Please enter a name.');
      return;
    }
    if (!formData.fileUrl) {
      setError('Please upload a file.');
      return;
    }

    try {
      await onSave({
        name: formData.name,
        folder_id: formData.folder_id,
        fileUrl: formData.fileUrl,
        fileType: formData.fileType
      });
      onClose();
    } catch (err) {
      console.error('Error saving presentation:', err);
      setError('Failed to save presentation. Please try again.');
    }
  };

  const isValid = formData.name.trim() && formData.fileUrl && (skipFolderSelect || formData.folder_id);

  if (!isOpen) return null;

  const modalContent = (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[9999] flex items-center justify-center p-4"
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-[#001233] border border-white/10 rounded-2xl w-full max-w-lg flex flex-col shadow-2xl"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-white/10">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-amber-600 flex items-center justify-center">
                <Presentation className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">
                  {presentation ? 'Edit Presentation' : 'Add Presentation'}
                </h2>
                <p className="text-sm text-gray-400">PDF, Word, PowerPoint, Excel, or image files</p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg transition-colors">
              <X className="w-5 h-5 text-gray-400" />
            </button>
          </div>

          <div className="flex-1 max-h-[60vh] overflow-y-auto">
            <div className="p-6 space-y-5">
              {/* Read-only banner */}
              {readOnly && (
                <div className="flex items-start gap-3 p-3 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-purple-400 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
                  <p className="text-sm text-purple-200">
                    This element is from a shared library and is <strong>view only</strong>. Copy it to your library to make changes.
                  </p>
                </div>
              )}

              {/* Error */}
              {error && (
                <div className="flex items-start gap-2 p-3 bg-red-500/20 border border-red-500/30 rounded-xl text-red-400 text-sm">
                  <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>{error}</span>
                </div>
              )}

              {/* Name */}
              <div>
                <label className="text-gray-400 mb-2 block text-sm">Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, name: e.target.value }));
                    setError('');
                  }}
                  disabled={readOnly}
                  placeholder="e.g., Q4 Sales Deck"
                  className={`w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 outline-none focus:border-orange-500 transition-colors ${readOnly ? 'opacity-60 cursor-not-allowed' : ''}`}
                />
              </div>

              {/* Folder */}
              {!skipFolderSelect && (
                <div>
                  <label className="text-gray-400 mb-2 block text-sm">Folder *</label>
                  <Select
                    value={formData.folder_id}
                    onValueChange={(v) => setFormData(prev => ({ ...prev, folder_id: v }))}
                    placeholder="Select folder..."
                  >
                    {folders?.map(f => (
                      <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>
                    ))}
                  </Select>
                </div>
              )}

              {/* File Upload */}
              <div>
                <label className="text-gray-400 mb-2 block text-sm">File *</label>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept={ACCEPTED_EXTENSIONS}
                  onChange={handleFileUpload}
                  style={{ display: 'none' }}
                />

                {formData.fileUrl ? (
                  // File uploaded - show preview
                  <div className="border border-white/20 rounded-xl p-4 bg-white/5">
                    <div className="flex items-center gap-3">
                      {formData.fileType === 'pdf' ? (
                        <FileText className="w-8 h-8 text-red-400" />
                      ) : (
                        <Image className="w-8 h-8 text-blue-400" />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-medium truncate">{formData.name || 'Uploaded file'}</p>
                        <p className="text-xs text-gray-400 uppercase">{formData.fileType}</p>
                      </div>
                      {!readOnly && (
                        <button
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                          disabled={isUploading}
                          className="px-3 py-1.5 text-sm border border-white/20 rounded-lg text-white hover:bg-white/10 transition-colors disabled:opacity-50"
                        >
                          Replace
                        </button>
                      )}
                    </div>
                  </div>
                ) : (
                  // No file - show upload button
                  !readOnly && <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isUploading}
                    className="w-full border-2 border-dashed border-white/20 rounded-xl p-8 text-center hover:border-orange-500/50 transition-colors"
                  >
                    {isUploading ? (
                      <div className="space-y-2">
                        <Loader2 className="w-10 h-10 text-orange-500 mx-auto animate-spin" />
                        <p className="text-white text-sm">
                          {isConverting ? 'Converting PowerPoint to PDF...' : 'Uploading...'}
                        </p>
                        {isConverting && (
                          <p className="text-xs text-gray-500">This may take a moment for large files</p>
                        )}
                      </div>
                    ) : (
                      <>
                        <Upload className="w-10 h-10 text-gray-500 mx-auto mb-3" />
                        <p className="text-white font-medium">Upload File</p>
                        <p className="text-xs text-gray-400 mt-1">
                          PDF, Word, PowerPoint, Excel, JPG, PNG, GIF &bull; Max {MAX_FILE_SIZE_MB}MB
                        </p>
                        <p className="text-xs text-gray-500 mt-2">
                          PowerPoint files are automatically converted to PDF
                        </p>
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 p-6 border-t border-white/10">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm border border-white/20 rounded-lg text-gray-300 hover:bg-white/10 transition-colors"
            >
              Cancel
            </button>
            {readOnly ? (onCopyToMyElements && (
              <button
                type="button"
                onClick={onCopyToMyElements}
                className="flex items-center gap-2 px-4 py-2 text-sm bg-gradient-to-r from-[#6a1fbf] to-[#00c853] rounded-lg text-white hover:opacity-90 transition-all"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                Copy to My Elements
              </button>
            )) : (
              <button
                type="button"
                onClick={handleSave}
                disabled={!isValid}
                className="flex items-center gap-2 px-4 py-2 text-sm bg-gradient-to-r from-orange-500 to-amber-600 rounded-lg text-white hover:from-orange-600 hover:to-amber-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Check className="w-4 h-4" />
                {presentation ? 'Save' : 'Add Presentation'}
              </button>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );

  return ReactDOM.createPortal(modalContent, document.body);
}
